<template>
  <v-container class="grey-lighten-3 full-height d-flex flex-column">
    <!-- Loading State -->
    <v-row v-if="loading" justify="center">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-2">Loading LC Amendment details...</p>
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-row v-else-if="!amendmentData || Object.keys(amendmentData).length === 0" justify="center">
      <v-col cols="12" class="text-center">
        <p class="text-grey">No LC Amendment data found</p>
        <v-btn color="primary" @click="goBack" class="mt-4">Go Back</v-btn>
      </v-col>
    </v-row>

    <!-- Printable Area -->
    <v-row v-else justify="center">
      <v-col cols="12" xl="10" lg="11" md="12">
        <div class="print-container" id="printable-area" style="width: 100%; max-width: 1000px; margin: auto; padding: 25px; background: #ffffff; position: relative; min-height: 600px; font-family: Arial, sans-serif;">

          <!-- Header Section -->
          <div class="header" style="text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px;">
            <img
                v-if="getCurrentCompany?.logo_thumb"
                :src="`${getFileServerUrl.replace(/\/$/, '')}/${getCurrentCompany.logo_thumb}`"
                alt="Company Logo"
                class="company-logo"
                style="width: 120px; margin-bottom: 10px;"
            />
            <div v-else class="logo-placeholder" style="margin-bottom: 10px;">
              <v-icon icon="mdi-domain" size="60" color="grey-darken-2"></v-icon>
            </div>
            <div class="company-info" style="font-size: 14px; margin-top: 5px;">
              <strong>{{ getCurrentCompany?.name || 'Company Name' }}</strong><br>
              {{ getCurrentCompany?.address || 'Company Address' }}<br>
              <span v-if="getCurrentCompany?.email">Email: {{ getCurrentCompany.email }}</span>
              <span v-if="getCurrentCompany?.mobile"> | Phone: {{ getCurrentCompany.mobile }}</span>
            </div>
          </div>

          <!-- Amendment Title -->
          <div class="lc-header-info" style="text-align: center; margin-bottom: 15px;">
            <h2 class="lc-title" style="font-size: 20px; font-weight: bold; margin-bottom: 8px;">LC AMENDMENT DETAILS</h2>
          </div>

          <!-- Amendment Summary Bar -->
          <div class="section-title" style="background: #f2f2f2; padding: 8px; margin-top: 20px; font-weight: bold; border-left: 4px solid #555; color: #333;">
            Amendment Information
          </div>
          <table class="info-table" style="width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 14px;">
            <tbody>
            <tr>
              <td style="width: 25%; background-color: #f5f5f5; font-weight: normal; padding: 6px 8px; border: 1px solid #ddd;"><strong>Amendment No:</strong></td>
              <td style="width: 25%; padding: 6px 8px; border: 1px solid #ddd;">{{ amendmentData.amendment_no || 'N/A' }}</td>
              <td style="width: 25%; background-color: #f5f5f5; font-weight: normal; padding: 6px 8px; border: 1px solid #ddd;"><strong>Amendment Date:</strong></td>
              <td style="width: 25%; padding: 6px 8px; border: 1px solid #ddd;">{{ formatDateView(amendmentData.amendment_date) }}</td>
            </tr>
            <tr>
              <td style="background-color: #f5f5f5; font-weight: normal; padding: 6px 8px; border: 1px solid #ddd;"><strong>Amendment Charge:</strong></td>
              <td style="padding: 6px 8px; border: 1px solid #ddd;">{{ formatCurrency(amendmentData.amendment_charge) }}</td>
              <td style="background-color: #f5f5f5; font-weight: normal; padding: 6px 8px; border: 1px solid #ddd;"><strong>Amendment Type(s):</strong></td>
              <td style="padding: 6px 8px; border: 1px solid #ddd;">
                <span
                    v-for="(type, idx) in (amendmentData.amendment_types || [])"
                    :key="idx"
                    style="display:inline-block; background:#607d8b; color:#fff; padding:2px 8px; border-radius:10px; font-size:12px; margin-right:4px;"
                >{{ type }}</span>
                <span v-if="!amendmentData.amendment_types || amendmentData.amendment_types.length === 0">N/A</span>
              </td>
            </tr>
            <tr v-if="amendmentData.remarks">
              <td style="background-color: #f5f5f5; font-weight: normal; padding: 6px 8px; border: 1px solid #ddd;"><strong>Remarks:</strong></td>
              <td colspan="3" style="padding: 6px 8px; border: 1px solid #ddd;">{{ amendmentData.remarks }}</td>
            </tr>
            </tbody>
          </table>

          <!-- LC Changes Table (field-level diff straight from lc_changes[]) -->
          <div v-if="amendmentData.lc_changes && amendmentData.lc_changes.length > 0">
            <div class="section-title" style="background: #f2f2f2; padding: 8px; margin-top: 20px; font-weight: bold; border-left: 4px solid #555; color: #333;">
              LC Field Changes
            </div>
            <table class="items-table" style="width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 14px;">
              <thead>
              <tr>
                <th style="width: 30%; border: 1px solid #999; padding: 8px; text-align: left; background: #eee; font-weight: bold; color: #333;">Field</th>
                <th style="width: 35%; border: 1px solid #999; padding: 8px; text-align: left; background: #ffe9e9; font-weight: bold; color: #333;">Before</th>
                <th style="width: 35%; border: 1px solid #999; padding: 8px; text-align: left; background: #e6f7ea; font-weight: bold; color: #333;">After</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(change, idx) in amendmentData.lc_changes" :key="idx">
                <td style="border: 1px solid #999; padding: 8px; text-transform: capitalize;">{{ String(change.field || '').replace(/_/g, ' ') }}</td>
                <td style="border: 1px solid #999; padding: 8px;" :style="String(change.before) !== String(change.after) ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ change.before ?? 'N/A' }}</td>
                <td style="border: 1px solid #999; padding: 8px;" :style="String(change.before) !== String(change.after) ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ change.after ?? 'N/A' }}</td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- LC Before vs LC After : all values read directly from amendmentData.lc_before / amendmentData.lc_after -->
          <div class="section-title" style="background: #f2f2f2; padding: 8px; margin-top: 20px; font-weight: bold; border-left: 4px solid #555; color: #333;">
            LC Details Comparison
          </div>
          <div style="display: flex; gap: 20px; margin-top: 10px;">

            <!-- LC Before -->
            <div style="flex: 1; border: 1px solid #ddd; border-radius: 4px; overflow: hidden;">
              <div style="background-color: #b0413e; color: #fff; padding: 10px 14px; font-weight: bold; font-size: 14px;">LC BEFORE AMENDMENT</div>
              <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                <tbody>
                <tr>
                  <td style="width: 45%; background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>LC No</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.lc_no !== amendmentData.lc_after?.lc_no ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ amendmentData.lc_before?.lc_no || 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>LC Status</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.lc_status !== amendmentData.lc_after?.lc_status ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ amendmentData.lc_before?.lc_status || 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>LC Amount</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.lc_amount !== amendmentData.lc_after?.lc_amount ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ formatCurrency(amendmentData.lc_before?.lc_amount) }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>LC Open Date</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.lc_open_date !== amendmentData.lc_after?.lc_open_date ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ amendmentData.lc_before?.lc_open_date ? formatDateView(amendmentData.lc_before.lc_open_date) : 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>Expiry Date</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.expiry_date !== amendmentData.lc_after?.expiry_date ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ amendmentData.lc_before?.expiry_date ? formatDateView(amendmentData.lc_before.expiry_date) : 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>Last Shipment Date</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.last_shipment_date !== amendmentData.lc_after?.last_shipment_date ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ amendmentData.lc_before?.last_shipment_date ? formatDateView(amendmentData.lc_before.last_shipment_date) : 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>Presentation Period (Days)</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.presentation_period !== amendmentData.lc_after?.presentation_period ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ amendmentData.lc_before?.presentation_period ?? 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>Margin %</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.margin_percent !== amendmentData.lc_after?.margin_percent ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ amendmentData.lc_before?.margin_percent ?? 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>Margin Amount</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.margin_amount !== amendmentData.lc_after?.margin_amount ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ formatCurrency(amendmentData.lc_before?.margin_amount) }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>Bank Charge</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.bank_charge !== amendmentData.lc_after?.bank_charge ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ formatCurrency(amendmentData.lc_before?.bank_charge) }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>Swift Charge</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.swift_charge !== amendmentData.lc_after?.swift_charge ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ formatCurrency(amendmentData.lc_before?.swift_charge) }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>Insurance</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.insurance !== amendmentData.lc_after?.insurance ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ formatCurrency(amendmentData.lc_before?.insurance) }}</td>
                </tr>
                <tr>
                  <td style="background: #faf5f5; padding: 6px 8px; border: 1px solid #eee;"><strong>Approval Status</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.approval_status !== amendmentData.lc_after?.approval_status ? 'background:#fff1f0; color:#b71c1c;' : ''">{{ amendmentData.lc_before?.approval_status || 'N/A' }}</td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- LC After -->
            <div style="flex: 1; border: 1px solid #ddd; border-radius: 4px; overflow: hidden;">
              <div style="background-color: #2e7d32; color: #fff; padding: 10px 14px; font-weight: bold; font-size: 14px;">LC AFTER AMENDMENT</div>
              <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                <tbody>
                <tr>
                  <td style="width: 45%; background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>LC No</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.lc_no !== amendmentData.lc_after?.lc_no ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ amendmentData.lc_after?.lc_no || 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>LC Status</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.lc_status !== amendmentData.lc_after?.lc_status ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ amendmentData.lc_after?.lc_status || 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>LC Amount</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.lc_amount !== amendmentData.lc_after?.lc_amount ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ formatCurrency(amendmentData.lc_after?.lc_amount) }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>LC Open Date</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.lc_open_date !== amendmentData.lc_after?.lc_open_date ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ amendmentData.lc_after?.lc_open_date ? formatDateView(amendmentData.lc_after.lc_open_date) : 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>Expiry Date</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.expiry_date !== amendmentData.lc_after?.expiry_date ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ amendmentData.lc_after?.expiry_date ? formatDateView(amendmentData.lc_after.expiry_date) : 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>Last Shipment Date</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.last_shipment_date !== amendmentData.lc_after?.last_shipment_date ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ amendmentData.lc_after?.last_shipment_date ? formatDateView(amendmentData.lc_after.last_shipment_date) : 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>Presentation Period (Days)</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.presentation_period !== amendmentData.lc_after?.presentation_period ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ amendmentData.lc_after?.presentation_period ?? 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>Margin %</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.margin_percent !== amendmentData.lc_after?.margin_percent ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ amendmentData.lc_after?.margin_percent ?? 'N/A' }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>Margin Amount</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.margin_amount !== amendmentData.lc_after?.margin_amount ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ formatCurrency(amendmentData.lc_after?.margin_amount) }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>Bank Charge</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.bank_charge !== amendmentData.lc_after?.bank_charge ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ formatCurrency(amendmentData.lc_after?.bank_charge) }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>Swift Charge</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.swift_charge !== amendmentData.lc_after?.swift_charge ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ formatCurrency(amendmentData.lc_after?.swift_charge) }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>Insurance</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.insurance !== amendmentData.lc_after?.insurance ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ formatCurrency(amendmentData.lc_after?.insurance) }}</td>
                </tr>
                <tr>
                  <td style="background: #f5faf6; padding: 6px 8px; border: 1px solid #eee;"><strong>Approval Status</strong></td>
                  <td style="padding: 6px 8px; border: 1px solid #eee;" :style="amendmentData.lc_before?.approval_status !== amendmentData.lc_after?.approval_status ? 'background:#f0fff2; color:#1b5e20; font-weight:bold;' : ''">{{ amendmentData.lc_after?.approval_status || 'N/A' }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- PI Before vs PI After : looping directly over amendmentData.pi_before / amendmentData.pi_after (paired by array index) -->
          <div v-if="(amendmentData.pi_before && amendmentData.pi_before.length > 0) || (amendmentData.pi_after && amendmentData.pi_after.length > 0)">
            <div class="section-title" style="background: #f2f2f2; padding: 8px; margin-top: 20px; font-weight: bold; border-left: 4px solid #555; color: #333;">
              Proforma Invoice Comparison
            </div>

            <div
                v-for="(piAfterItem, piIndex) in (amendmentData.pi_after || amendmentData.pi_before)"
                :key="piAfterItem.id || piIndex"
                class="pi-section"
                style="margin-top: 20px; page-break-inside: avoid;"
            >

              <!-- PI identity bar (pulled straight from pi_after, falling back to pi_before) -->
              <div style="background-color: #607d8b; color: white; padding: 12px 16px; border-radius: 4px 4px 0 0; display:flex; gap:24px; flex-wrap:wrap;">
                <span style="font-size: 14px; color: white;"><strong>PI No:</strong> {{ (amendmentData.pi_after?.[piIndex]?.pi_no) || (amendmentData.pi_before?.[piIndex]?.pi_no) || 'N/A' }}</span>
                <span style="font-size: 14px; color: white;"><strong>PI Date:</strong> {{ formatDateView((amendmentData.pi_after?.[piIndex]?.pi_date) || (amendmentData.pi_before?.[piIndex]?.pi_date)) }}</span>
                <span style="font-size: 14px; color: white;"><strong>Currency:</strong> {{ (amendmentData.pi_after?.[piIndex]?.currency?.code) || (amendmentData.pi_before?.[piIndex]?.currency?.code) || 'N/A' }}</span>
              </div>

              <!-- Before / After PI header details -->
              <div style="display: flex; gap: 20px; background-color: white; border: 1px solid #ddd; border-top: none; padding: 16px;">
                <div style="flex: 1;">
                  <div style="font-weight:bold; color:#b71c1c; margin-bottom:8px; font-size:13px;">BEFORE</div>

                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">PI No:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.pi_no !== amendmentData.pi_after?.[piIndex]?.pi_no ? 'color:#b71c1c; font-weight:bold;' : 'color:#333;'">{{ amendmentData.pi_before?.[piIndex]?.pi_no || 'N/A' }}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">PI Date:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.pi_date !== amendmentData.pi_after?.[piIndex]?.pi_date ? 'color:#b71c1c; font-weight:bold;' : 'color:#333;'">{{ amendmentData.pi_before?.[piIndex]?.pi_date ? formatDateView(amendmentData.pi_before[piIndex].pi_date) : 'N/A' }}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">Validity:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.validity !== amendmentData.pi_after?.[piIndex]?.validity ? 'color:#b71c1c; font-weight:bold;' : 'color:#333;'">{{ amendmentData.pi_before?.[piIndex]?.validity ? formatDateView(amendmentData.pi_before[piIndex].validity) : 'N/A' }}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">Approval Status:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.approval_status !== amendmentData.pi_after?.[piIndex]?.approval_status ? 'color:#b71c1c; font-weight:bold;' : 'color:#333;'">{{ amendmentData.pi_before?.[piIndex]?.approval_status || 'N/A' }}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">Sub Total:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.sub_total !== amendmentData.pi_after?.[piIndex]?.sub_total ? 'color:#b71c1c; font-weight:bold;' : 'color:#333;'">{{ formatCurrency(amendmentData.pi_before?.[piIndex]?.sub_total, amendmentData.pi_before?.[piIndex]?.currency?.code) }}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">Total Amount:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.total_amount !== amendmentData.pi_after?.[piIndex]?.total_amount ? 'color:#b71c1c; font-weight:bold;' : 'color:#333;'">{{ formatCurrency(amendmentData.pi_before?.[piIndex]?.total_amount, amendmentData.pi_before?.[piIndex]?.currency?.code) }}</span>
                  </div>
                </div>

                <div style="flex: 1;">
                  <div style="font-weight:bold; color:#1b5e20; margin-bottom:8px; font-size:13px;">AFTER</div>

                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">PI No:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.pi_no !== amendmentData.pi_after?.[piIndex]?.pi_no ? 'color:#1b5e20; font-weight:bold;' : 'color:#333;'">{{ amendmentData.pi_after?.[piIndex]?.pi_no || 'N/A' }}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">PI Date:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.pi_date !== amendmentData.pi_after?.[piIndex]?.pi_date ? 'color:#1b5e20; font-weight:bold;' : 'color:#333;'">{{ amendmentData.pi_after?.[piIndex]?.pi_date ? formatDateView(amendmentData.pi_after[piIndex].pi_date) : 'N/A' }}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">Validity:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.validity !== amendmentData.pi_after?.[piIndex]?.validity ? 'color:#1b5e20; font-weight:bold;' : 'color:#333;'">{{ amendmentData.pi_after?.[piIndex]?.validity ? formatDateView(amendmentData.pi_after[piIndex].validity) : 'N/A' }}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">Approval Status:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.approval_status !== amendmentData.pi_after?.[piIndex]?.approval_status ? 'color:#1b5e20; font-weight:bold;' : 'color:#333;'">{{ amendmentData.pi_after?.[piIndex]?.approval_status || 'N/A' }}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">Sub Total:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.sub_total !== amendmentData.pi_after?.[piIndex]?.sub_total ? 'color:#1b5e20; font-weight:bold;' : 'color:#333;'">{{ formatCurrency(amendmentData.pi_after?.[piIndex]?.sub_total, amendmentData.pi_after?.[piIndex]?.currency?.code) }}</span>
                  </div>
                  <div style="display:flex; justify-content:space-between; padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px;">
                    <span style="color:#555;">Total Amount:</span>
                    <span :style="amendmentData.pi_before?.[piIndex]?.total_amount !== amendmentData.pi_after?.[piIndex]?.total_amount ? 'color:#1b5e20; font-weight:bold;' : 'color:#333;'">{{ formatCurrency(amendmentData.pi_after?.[piIndex]?.total_amount, amendmentData.pi_after?.[piIndex]?.currency?.code) }}</span>
                  </div>
                </div>
              </div>

              <!-- PI Items Before (directly from amendmentData.pi_before[piIndex].details) -->
              <div style="margin-top: 15px;">
                <div style="background:#ffe9e9; padding:6px 10px; font-weight:bold; font-size:13px; color:#b71c1c; border-left:4px solid #b71c1c;">Items Before</div>
                <table style="width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 13px;">
                  <thead>
                  <tr>
                    <th style="width: 6%; border: 1px solid #999; padding: 6px; background: #eee;">SL</th>
                    <th style="width: 14%; border: 1px solid #999; padding: 6px; background: #eee;">Product</th>
                    <th style="width: 12%; border: 1px solid #999; padding: 6px; background: #eee;">HS Code</th>
                    <th style="width: 14%; border: 1px solid #999; padding: 6px; background: #eee; text-align:right;">Qty</th>
                    <th style="width: 14%; border: 1px solid #999; padding: 6px; background: #eee; text-align:right;">Shipped Qty</th>
                    <th style="width: 14%; border: 1px solid #999; padding: 6px; background: #eee; text-align:right;">Rate</th>
                    <th style="width: 14%; border: 1px solid #999; padding: 6px; background: #eee; text-align:right;">Amount</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(item, idx) in (amendmentData.pi_before?.[piIndex]?.details || [])" :key="'ib-'+idx">
                    <td style="border: 1px solid #999; padding: 6px;">{{ idx + 1 }}</td>
                    <td style="border: 1px solid #999; padding: 6px;">{{ item.product_id ?? 'N/A' }}</td>
                    <td style="border: 1px solid #999; padding: 6px;">{{ item.hs_code || 'N/A' }}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align:right;">{{ Number(item.qty || 0).toFixed(2) }}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align:right;">{{ item.shipped_qty ?? '0' }}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align:right;">{{ formatCurrency(item.rate, amendmentData.pi_before?.[piIndex]?.currency?.code) }}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align:right;">{{ formatCurrency(item.amount, amendmentData.pi_before?.[piIndex]?.currency?.code) }}</td>
                  </tr>
                  <tr v-if="!amendmentData.pi_before?.[piIndex]?.details || amendmentData.pi_before[piIndex].details.length === 0">
                    <td colspan="7" style="border: 1px solid #999; padding: 6px; text-align:center; color:#999;">No items found</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <!-- PI Items After (directly from amendmentData.pi_after[piIndex].details) -->
              <div style="margin-top: 15px;">
                <div style="background:#e6f7ea; padding:6px 10px; font-weight:bold; font-size:13px; color:#1b5e20; border-left:4px solid #1b5e20;">Items After</div>
                <table style="width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 13px;">
                  <thead>
                  <tr>
                    <th style="width: 6%; border: 1px solid #999; padding: 6px; background: #eee;">SL</th>
                    <th style="width: 14%; border: 1px solid #999; padding: 6px; background: #eee;">Product</th>
                    <th style="width: 12%; border: 1px solid #999; padding: 6px; background: #eee;">HS Code</th>
                    <th style="width: 14%; border: 1px solid #999; padding: 6px; background: #eee; text-align:right;">Qty</th>
                    <th style="width: 14%; border: 1px solid #999; padding: 6px; background: #eee; text-align:right;">Shipped Qty</th>
                    <th style="width: 14%; border: 1px solid #999; padding: 6px; background: #eee; text-align:right;">Rate</th>
                    <th style="width: 14%; border: 1px solid #999; padding: 6px; background: #eee; text-align:right;">Amount</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(item, idx) in (amendmentData.pi_after?.[piIndex]?.details || [])" :key="'ia-'+idx">
                    <td style="border: 1px solid #999; padding: 6px;">{{ idx + 1 }}</td>
                    <td style="border: 1px solid #999; padding: 6px;">{{ item.product_id ?? 'N/A' }}</td>
                    <td style="border: 1px solid #999; padding: 6px;">{{ item.hs_code || 'N/A' }}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align:right;">{{ Number(item.qty || 0).toFixed(2) }}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align:right;">{{ item.shipped_qty ?? '0' }}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align:right;">{{ formatCurrency(item.rate, amendmentData.pi_after?.[piIndex]?.currency?.code) }}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align:right;">{{ formatCurrency(item.amount, amendmentData.pi_after?.[piIndex]?.currency?.code) }}</td>
                  </tr>
                  <tr v-if="!amendmentData.pi_after?.[piIndex]?.details || amendmentData.pi_after[piIndex].details.length === 0">
                    <td colspan="7" style="border: 1px solid #999; padding: 6px; text-align:center; color:#999;">No items found</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <!-- PI Summary Before / After -->
              <div style="display:flex; gap:20px; margin-top:15px;">
                <table style="flex:1; border-collapse: collapse; font-size: 14px;">
                  <tbody>
                  <tr>
                    <td style="border: 1px solid #999; padding: 8px; background:#faf5f5;"><strong>Sub Total (Before)</strong></td>
                    <td style="border: 1px solid #999; padding: 8px; text-align:right;">{{ formatCurrency(amendmentData.pi_before?.[piIndex]?.sub_total, amendmentData.pi_before?.[piIndex]?.currency?.code) }}</td>
                  </tr>
                  </tbody>
                </table>
                <table style="flex:1; border-collapse: collapse; font-size: 14px;">
                  <tbody>
                  <tr>
                    <td style="border: 1px solid #999; padding: 8px; background:#f5faf6;"><strong>Sub Total (After)</strong></td>
                    <td style="border: 1px solid #999; padding: 8px; text-align:right;">{{ formatCurrency(amendmentData.pi_after?.[piIndex]?.sub_total, amendmentData.pi_after?.[piIndex]?.currency?.code) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

            </div>
          </div>
          <div v-else class="pi-section" style="margin-top: 20px;">
            <div class="section-title" style="background: #f2f2f2; padding: 8px; font-weight: bold; border-left: 4px solid #555; color: #333;">Proforma Invoice Comparison</div>
            <p style="text-align:center; color:#999; padding: 16px;">No Proforma Invoice data associated with this amendment</p>
          </div>

          <!-- Signature Section -->
          <div class="signature-area mt-8" style="margin-top: 50px; display: flex; justify-content: space-between; gap: 20px; page-break-inside: avoid;">
            <div class="signature-box" style="width: 220px; text-align: center; font-size: 14px;">
              <div class="signature-line" style="border-top: 1px solid #000; margin-top: 40px; padding-top: 5px;"></div>
              <p class="text-center mt-2">Prepared By</p>
            </div>
            <div class="signature-box" style="width: 220px; text-align: center; font-size: 14px;">
              <div class="signature-line" style="border-top: 1px solid #000; margin-top: 40px; padding-top: 5px;"></div>
              <p class="text-center mt-2">Checked By</p>
            </div>
            <div class="signature-box" style="width: 220px; text-align: center; font-size: 14px;">
              <div class="signature-line" style="border-top: 1px solid #000; margin-top: 40px; padding-top: 5px;"></div>
              <p class="text-center mt-2">Approved By</p>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Action Toolbar at Bottom (Hidden on Print) -->
    <v-row class="d-print-none mb-4" justify="center" v-if="!loading && amendmentData">
      <v-col cols="12">
        <div class="text-center">
          <v-btn color="primary" class="mr-2" @click="printPage">
            <v-icon left>mdi-printer</v-icon> Print
          </v-btn>
          <v-btn color="success" @click="downloadPDF">
            <v-icon left>mdi-file-pdf-box</v-icon> Download PDF
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import html2pdf from "html2pdf.js";
import { ref, onMounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import { useStore } from "vuex";
import { formatDateView, formatDateDash } from "@/plugins/helpers";

export default {
  name: "ViewLcImportLcAmendment",
  methods: {
    formatDateView,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const loading = ref(false);
    const amendmentData = ref({});

    // Only two tiny formatting helpers remain in script — everything else
    // (which fields to compare, which PI pairs with which, item loops, diffs)
    // is written directly in the template against amendmentData.* paths.
    const formatCurrency = (amount, currencyCode = null) => {
      if (!amount && amount !== 0) return "0.00";
      const formatted = Number(amount).toFixed(2);
      const currency = currencyCode || amendmentData.value?.lc_after?.currency?.code || "USD";
      return `${currency} ${formatted}`;
    };

    const loadAmendmentData = async (id) => {
      if (!id) {
        toast.error("No LC Amendment ID provided");
        router.push({ path: "/lc/import-lc-amendment-record" });
        return;
      }

      loading.value = true;
      try {
        const response = await store.dispatch("getLcImportLcAmendmentById", id);
        amendmentData.value = response?.data ?? response;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load LC Amendment data");
        router.push({ path: "/lc/import-lc-amendment-record" });
      } finally {
        loading.value = false;
      }
    };

    const goBack = () => {
      router.push({ path: "/lc/import-lc-amendment-record" });
    };

    const downloadPDF = async () => {
      const element = document.getElementById("printable-area");
      const opt = {
        margin: [0.5, 0.5],
        filename: `lc-amendment-${amendmentData.value.lc_master?.lc_no || amendmentData.value.amendment_no}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "cm", format: "a4", orientation: "portrait" },
      };
      await html2pdf().set(opt).from(element).save();
    };

    const printPage = () => {
      const printContent = document.getElementById("printable-area").innerHTML;
      const printWindow = window.open("", "_blank", `width=${screen.width}, height=${screen.height}, left=0, top=0`);

      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>LC Amendment - ${amendmentData.value.lc_master?.lc_no || amendmentData.value.amendment_no || ''}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              background: #fff;
              margin: 0;
              padding: 0;
            }
            @media print {
              .d-print-none { display: none !important; }
              .pi-section { page-break-inside: avoid; }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.moveTo(0, 0);
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      }, 1000);
    };

    onMounted(() => {
      if (route.params.id) {
        loadAmendmentData(route.params.id);
      } else {
        toast.error("No LC Amendment ID provided");
        router.push({ path: "/lc/import-lc-amendment-record" });
      }
    });

    return {
      loading,
      amendmentData,
      formatCurrency,
      goBack,
      downloadPDF,
      printPage,
      getCurrentCompany: computed(() => store.getters.getCurrentCompany),
      getFileServerUrl: computed(() => store.getters.getFileServerUrl),
    };
  },
};
</script>

<style scoped>
.full-height {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

@media print {
  .d-print-none {
    display: none !important;
  }
}
</style>